import { HERO_TOOL_TIMELINES, type DemoFocus } from "./hero-tool-demos";

type Pose = { x: number; y: number; scale: number };
const MOVE_MS = 1100;
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const ease = (time: number) => { const t = clamp(time / MOVE_MS, 0, 1); return t * t * (3 - 2 * t); };
const mix = (a: number, b: number, progress: number) => a + (b - a) * progress;
const blend = (a: Pose, b: Pose, progress: number): Pose => ({ x: mix(a.x, b.x, progress), y: mix(a.y, b.y, progress), scale: mix(a.scale, b.scale, progress) });

function framePose(frame: DemoFocus, imageRatio: number, viewportRatio: number): Pose {
  const scale = Math.min(4, 88 / frame.width, (imageRatio / viewportRatio * 74) / frame.height);
  const viewHeight = 100 * imageRatio / viewportRatio;
  return {
    scale,
    x: scale < 1 ? (100 - 100 * scale) / 2 : clamp(50 - (frame.x + frame.width / 2) * scale, 100 - 100 * scale, 0),
    y: 100 * scale < viewHeight ? (viewHeight - 100 * scale) / 2 : clamp(viewHeight / 2 - (frame.y + frame.height / 2) * scale, viewHeight - 100 * scale, 0),
  };
}

export function getToolCamera(tool: number, elapsed: number, imageRatio: number, viewportRatio: number, reduced = false, start = 0) {
  const cues = HERO_TOOL_TIMELINES[tool].cues.filter((cue) => cue.start >= start);
  let cue = cues[0];
  let from = framePose(cue.frame, imageRatio, viewportRatio);
  let to = from;
  let previousTarget = cue.target;
  for (const next of cues.slice(1)) {
    if (next.start > elapsed) break;
    from = blend(from, to, ease(next.start - cue.start));
    to = framePose(next.frame, imageRatio, viewportRatio);
    previousTarget = cue.target ?? previousTarget;
    cue = next;
  }
  const age = elapsed - cue.start;
  const progress = reduced ? 1 : ease(age);
  const pose = reduced ? to : blend(from, to, progress);
  const project = (x: number, y: number) => ({ x: pose.x + x * pose.scale, y: (pose.y + y * pose.scale) * viewportRatio / imageRatio });
  const target = cue.target;
  const point = target && { x: target.x + target.width * .88, y: target.y + target.height * .8 };
  const previous = previousTarget && { x: previousTarget.x + previousTarget.width * .88, y: previousTarget.y + previousTarget.height * .8 };
  return {
    pose, cue,
    spotlight: target && { ...project(target.x, target.y), width: target.width * pose.scale, height: target.height * pose.scale * viewportRatio / imageRatio },
    pointer: point && project(mix(previous?.x ?? point.x, point.x, progress), mix(previous?.y ?? point.y, point.y, progress)),
    opacity: reduced || start > 0 && cue.start === start ? 1 : clamp(age / 450, 0, 1),
    pulse: reduced || start > 0 && cue.start === start ? 1 : clamp((age - MOVE_MS) / 800, 0, 1),
  };
}
